import React, { useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Typography,
  Box,
  Divider,
  CircularProgress,
} from "@mui/material";
import * as XLSX from "xlsx";
import { BaseTable } from "../BaseTable/BaseTable";
import { teamBulkColumns } from "./teams.constants";
import { bulkTeamUploadUrl } from "src/react-query/endPoints";
import axios from "axios";
import Token from "src/util/Token";
import { useDispatch } from "react-redux";
import { alertSlice } from "src/store/alert/alert.slice";
import enums from "src/util/enums";

const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const fetchGeocode = async (address) => {
  const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
    address
  )}&key=${apiKey}`;
  const response = await fetch(url);
  const data = await response.json();
  if (data.results.length > 0) {
    const location = data.results[0].geometry.location;
    const components = data.results[0].address_components;
    return {
      lat: location.lat,
      lng: location.lng,
      city: getComponent(components, "locality"),
      state: getComponent(components, "administrative_area_level_1"),
      country: getComponent(components, "country"),
      street: data.results[0].formatted_address,
    };
  }
  return {};
};

const getComponent = (components, type) => {
  const comp = components.find((c) => c.types.includes(type));
  return comp ? comp.long_name : "";
};

export const TeamBulkModal = ({ open, onClose, onUpload }) => {
  const [uploadedData, setUploadedData] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [fileName, setFileName] = useState("");
  const [uploadMessage, setUploadMessage] = useState(null);
  const [validating, setValidating] = useState(false);
  const [uploading, setUploading] = useState(false); // <-- Add uploading state

  const dispatch = useDispatch();
  const {
    actions: { setAlert },
  } = alertSlice;

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setFileName(file.name);
      setValidating(true);
      const reader = new FileReader();
      reader.onload = async (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(sheet);

        // Map and validate data
        const validatedData = await Promise.all(
          jsonData.map(async (row, index) => {
            // Normalize keys
            const normalizedRow = {};
            Object.keys(row).forEach((key) => {
              normalizedRow[key.trim()] = row[key];
            });

            const errors = [];
            const teamName = normalizedRow["Name"];
            const addressString = normalizedRow["Address"];
            const stateString = normalizedRow["State"];
            const townString = normalizedRow["City"];

            if (!addressString)
              errors.push(`Row ${index + 1}: Missing "Shipping Address"`);
            if (!teamName) errors.push(`Row ${index + 1}: Missing "Name"`);            

            let geocode = {};
            if (addressString) {
              geocode = await fetchGeocode(addressString);
              if (!geocode.lat || !geocode.lng) {
                errors.push(
                  `Row ${index + 1}: Invalid address, could not fetch lat/lng`
                );
              }
            }

            const team = {
              id: `${
                teamName
              }-${index}-${Date.now()}`,
              name: teamName,
              description: teamName + ",\n"  + addressString,
              address: {
                street: geocode.street || addressString,
                city: geocode.city || townString,
                state: geocode.state || stateString,
                country: geocode.country,
                zipCode: normalizedRow["Zip Code"] || "",
                locationLatitude: geocode.lat,
                locationLongitude: geocode.lng,
              },
            };

            if (!team.name)
              errors.push(
                `Row ${index + 1}: Missing "Trade Name" or "Dealer/Branch Code"`
              );

            return { team, errors };
          })
        );

        // Separate valid data and errors
        const validTeams = validatedData
          .filter((item) => item.errors.length === 0)
          .map((item) => item.team);
        const validationErrors = validatedData
          .filter((item) => item.errors.length > 0)
          .flatMap((item) => item.errors);

        setUploadedData(validTeams);
        setValidationErrors(validationErrors);
        setValidating(false);
      };
      reader.readAsArrayBuffer(file);
    }
  };

  const CHUNK_SIZE = 100;

  const handleBulkUpload = async () => {
    const tokenObj = Token.get("_flst");
    const tokenStr =
      typeof tokenObj === "string"
        ? tokenObj
        : tokenObj?.accessToken || tokenObj?.token || "";
    setUploadMessage(null);
    setUploading(true);

    try {
      let allSuccess = true;
      let errorMessages = [];
      // Split uploadedData into chunks of 100
      for (let i = 0; i < uploadedData.length; i += CHUNK_SIZE) {
        const chunk = uploadedData.slice(i, i + CHUNK_SIZE);
        const response = await axios.post(bulkTeamUploadUrl, chunk, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${tokenStr}`,
          },
        });
        const result = response.data;
        if (!result.success) {
          allSuccess = false;
          errorMessages.push(result.message || `Chunk ${i / CHUNK_SIZE + 1} upload failed.`);
        }
      }

      if (allSuccess) {
        setUploadMessage({
          type: "success",
          text: "Teams uploaded successfully!",
        });
        setUploadedData([]);
        setFileName("");
        dispatch(
          setAlert({
            open: true,
            message: "Teams added successfully",
            severity: enums.SEVERITY.SUCCESS,
          })
        );
        setTimeout(() => {
          setUploadMessage(null);
          setUploading(false);
          onClose();
        }, 1500);
        onUpload();
      } else {
        setUploadMessage({
          type: "error",
          text: errorMessages.join(" "),
        });
        setUploading(false);
      }
    } catch (error) {
      setUploadMessage({
        type: "error",
        text:
          error?.response?.data?.message || "Upload failed. Please try again.",
      });
      setUploading(false);
    }
  };

  const handleClose = () => {
    setUploadedData([]);
    setValidationErrors([]);
    setFileName("");
    setUploadMessage(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6" fontWeight="bold">
          Add Bulk Teams
        </Typography>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ py: 3 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Upload an Excel file to add multiple teams at once. Ensure the file
            contains the required fields: <strong>Name</strong> and{" "}
            <strong>Address</strong>.
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Button
              variant="outlined"
              component="label"
              sx={{ textTransform: "none" }}
              disabled={validating}
            >
              Choose File
              <input
                type="file"
                accept=".xlsx"
                onChange={handleFileUpload}
                hidden
                disabled={validating}
              />
            </Button>
            <Typography variant="body2" color="textSecondary">
              {fileName || "No file chosen"}
            </Typography>
            {validating && (
              <Box sx={{ display: "flex", alignItems: "center", ml: 2 }}>
                <CircularProgress size={24} />
                <Typography sx={{ ml: 1 }}>Validating...</Typography>
              </Box>
            )}
          </Box>
        </Box>
        {validationErrors.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="error" sx={{ mb: 1 }}>
              Validation Errors:
            </Typography>
            <ul style={{ color: "red", margin: 0, paddingLeft: "1.5rem" }}>
              {validationErrors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </Box>
        )}
        {!validating && uploadedData.length > 0 && (
          <BaseTable
            rows={uploadedData}
            columns={teamBulkColumns()}
            pageSize={5}
            getRowId={(row) => row.id}
          />
        )}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} variant="outlined" disabled={uploading}>
          Cancel
        </Button>
        <Box sx={{ position: "relative", display: "inline-block" }}>
          <Button
            variant="contained"
            onClick={handleBulkUpload}
            disabled={
              uploadedData.length === 0 ||
              validationErrors.length > 0 ||
              validating ||
              uploading
            }
          >
            Upload
          </Button>
          {uploading && (
            <CircularProgress
              size={24}
              sx={{
                color: "primary.main",
                position: "absolute",
                top: "50%",
                left: "50%",
                marginTop: "-12px",
                marginLeft: "-12px",
              }}
            />
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};
